<script lang="ts">
import {defineComponent, PropType} from 'vue'
import Select, {SelectFilterEvent} from 'primevue/select'
import {fetchWrapper} from "@/helpers/fetchWrapper"
import backendRoutes from "@/backendRoutes"
import {ApiPlatformParser} from "@/helpers/apiPlatformParser"
import debounce from "debounce"
import {Course} from "@/interfaces/course"
import isNull from "@/helpers/isNull"
import MultiSelect from 'primevue/multiselect'

export default defineComponent({
  name: "CourseSelect",
  components: {
    Select,
    MultiSelect
  },
  props: {
    invalid: {
      type: Boolean,
      required: false,
      default: false
    },
    modelValue: {
      type: [Object, Array] as PropType<Course | Course[] | null>,
      required: false,
      default: null
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    listRoute: {
      type: String,
      required: false,
      default: backendRoutes.course_register_list
    },
    requestParams: {
      type: Object as PropType<Record<string, any>>,
      required: false,
      default: undefined
    }
  },
  data() {
    return {
      courses: [] as Course[],
      totalItemsCount: 0,
      search: '' as string,
      debouncedFetchCourseListApi: debounce(() => {
        this.fetchCourseList()
      }, 300),
      isLoading: true as boolean,
      courseSelectedByUrl: this.$route.query.courseSelect ? JSON.parse(String(this.$route.query.courseSelect)) : null as Course | null
    }
  },
  mounted() {
    if(this.courseSelectedByUrl) {
      this.$emit('update:modelValue', this.courseSelectedByUrl)
    }
    this.fetchCourseList()
  },
  watch: {
    requestParams: {
      deep: true,
      handler() {
        this.fetchCourseList()
      }
    }
  },
  methods: {
    isNull,
    async fetchCourseList() {
      this.isLoading = true
      const response = await fetchWrapper.get(this.listRoute, undefined,
          {
            pagination: !this.multiple,
            search: this.search,
            ...(this.requestParams ?? {}),
          })
      const apiPlatformParser = new ApiPlatformParser(response, ApiPlatformParser.LIST_TYPE)
      this.courses = apiPlatformParser.getData()
      this.courses = this.courses.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
      this.totalItemsCount = apiPlatformParser.getTotalItemCount()
      this.isLoading = false

      if(this.multiple && Array.isArray(this.modelValue) && this.modelValue.length > 0) {
        let newValues: Course[] = [];
        (this.modelValue as Course[]).forEach((course: Course) => {
          let found = this.courses.find(c => c['@id'] === course['@id'])
          if(found) {
            newValues.push(found)
          }
        })
        this.$emit('update:modelValue', newValues)
      }
    },
    searchTipping(event: SelectFilterEvent) {
      if(!this.multiple && this.totalItemsCount > this.courses.length) {
        this.search = event.value
        this.debouncedFetchCourseListApi()
      }
    },
    emitValue(value: Course|Course[]) {
      this.$emit('update:modelValue', value)
    },
    getDayOfWeek(day: number) {
      return this.$t('global.days.' + day)
    },
    getName(course: Course) {
      const startYear = course.startDate?.split('/').pop()
      const endYear = course.endDate?.split('/').pop()
      return `${course.name} - ${this.getDayOfWeek(course.day as number)} ${course.startHour} - ${startYear}-${endYear}`
    },
  },
  emits: ['update:modelValue'],
})
</script>

<template>
  <Select v-if="!multiple" @filter="searchTipping" :modelValue="modelValue" filter :filterFields="['name','id']"
          :invalid="invalid" :options="courses" @update:modelValue="emitValue" :showClear="!isNull(modelValue)">
    <template #value="slotProps">
      <div v-if="slotProps.value">{{ getName(slotProps.value) }}</div>
    </template>
    <template #option="slotProps">
      <div>{{ getName(slotProps.option) }}</div>
    </template>
    <template #emptyfilter>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.course_select.empty") }}</p>
      </div>
    </template>
    <template #empty>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.course_select.empty") }}</p>
      </div>
    </template>
  </Select>
  <MultiSelect ref="multiselect" v-else @filter="searchTipping" :modelValue="modelValue" filter :filterFields="['name','id']"
               :invalid="invalid" :options="courses" @update:modelValue="emitValue" :dataKey="'id'">
    <template #value="slotProps">
      <div v-if="slotProps.value">
        {{ slotProps.value.length == 1 ? getName(slotProps.value[0]) : slotProps.value.length + ' ' + $t('admin_components.course_select.course_selected') }}
      </div>
    </template>
    <template #option="slotProps">
      <div>{{ slotProps.option.name }} - {{ getDayOfWeek(slotProps.option.day) }} {{ slotProps.option.startHour }}
        <ul v-if="slotProps.option.galaShows && slotProps.option.galaShows.length > 0" class="infos">
          <li v-for="galaShow in slotProps.option.galaShows" :key="galaShow['@id']">
            {{ galaShow.name }} - {{ galaShow.date }}
          </li>
        </ul>
      </div>
    </template>
    <template #emptyfilter>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.course_select.empty") }}</p>
      </div>
    </template>
    <template #empty>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.course_select.empty") }}</p>
      </div>
    </template>
  </MultiSelect>
</template>

<style scoped lang="scss">
  @use "@/styles/global/variables.scss" as *;
  .infos {
    font-size: 0.8em;
    color: $low_information_color;
    margin-left: 1rem;
    line-height: 1.2rem;
    margin-top: 0.2rem;
  }
</style>
